/**
 * API Endpoint Documentation Node Component
 * Renders API endpoint documentation with grouped endpoints by HTTP method
 *
 * DUAL-MODE DESIGN:
 * - Export-safe foundation: Light code backgrounds, professional spacing for PNG/SVG
 * - Interactive enhancements: Dark code blocks, hover states via @media (hover: hover)
 */

import { memo, useState, useEffect } from 'react'
import { NodeProps, NodeResizer, useUpdateNodeInternals } from '@xyflow/react'
import { NodeConnectionHandles } from './NodeConnectionHandles'
import type { APIEndpointContent } from '../../types/schema'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface APIEndpointNodeData {
  label: string
  endpointContent?: APIEndpointContent
  style?: {
    // Colors
    headerBgColor?: string    // Header background
    headerTextColor?: string  // Header text
    bgColor?: string          // Body background
    borderColor?: string
    textColor?: string
    // Border
    borderWidth?: number
    borderStyle?: string
    // Opacity
    fillOpacity?: number
    textOpacity?: number
    // Text formatting
    fontSize?: number
    fontWeight?: string
    fontFamily?: string
    textAlign?: string
    verticalAlign?: string
    lineHeight?: number
    textDecoration?: string
    // Text behavior
    wordWrap?: boolean
    textOverflow?: string
    textPadding?: number
    letterSpacing?: number
  }
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

// HTTP method colors - Clean UI with soft tinted backgrounds
const methodColors: Record<string, { bg: string, text: string }> = {
  GET: { bg: '#dbeafe', text: '#1e40af' },      // Soft blue
  POST: { bg: '#d1fae5', text: '#065f46' },     // Soft green
  PUT: { bg: '#fed7aa', text: '#92400e' },      // Soft orange
  DELETE: { bg: '#fee2e2', text: '#991b1b' },   // Soft red
  PATCH: { bg: '#f3e8ff', text: '#6b21a8' }     // Soft purple
}

function APIEndpointNode({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as APIEndpointNodeData
  const endpointContent = nodeData.endpointContent
  const updateNodeInternals = useUpdateNodeInternals()

  // Collapsible sections state (one per HTTP method group)
  const [expandedMethods, setExpandedMethods] = useState<Record<string, boolean>>({
    GET: true,
    POST: true,
    PUT: true,
    DELETE: true,
    PATCH: true
  })

  // Force React Flow to measure the node after mount
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, updateNodeInternals])

  const toggleMethod = (method: string) => {
    setExpandedMethods(prev => ({ ...prev, [method]: !prev[method] }))
    // Update node dimensions after state change
    setTimeout(() => updateNodeInternals(id), 0)
  }

  // Apply user-defined styles or defaults
  // Read from root level first (Properties Panel writes here), then fallback to nested style

  // Colors - Clean UI design with soft purple tint
  const headerBg = (nodeData as any).headerBgColor !== undefined ? (nodeData as any).headerBgColor : (nodeData.style?.headerBgColor || '#faf5ff')
  const headerTextColor = (nodeData as any).headerTextColor || nodeData.style?.headerTextColor || '#581c87'
  const bodyBg = (nodeData as any).bgColor !== undefined ? (nodeData as any).bgColor : (nodeData.style?.bgColor || '#ffffff')
  const textColor = (nodeData as any).textColor || nodeData.style?.textColor || '#1f2937'
  const borderColor = (nodeData as any).borderColor !== undefined ? (nodeData as any).borderColor : (nodeData.style?.borderColor || '#e9d5ff')

  // Border - Subtle 1px for clean design
  const borderWidth = (nodeData as any).borderWidth !== undefined ? (nodeData as any).borderWidth : (nodeData.style?.borderWidth !== undefined ? nodeData.style.borderWidth : 1)
  const borderStyle = (nodeData as any).borderStyle || nodeData.style?.borderStyle || 'solid'

  // Opacity
  const fillOpacity = (nodeData as any).fillOpacity !== undefined ? (nodeData as any).fillOpacity : (nodeData.style?.fillOpacity !== undefined ? nodeData.style.fillOpacity : 1)
  const textOpacity = (nodeData as any).textOpacity !== undefined ? (nodeData as any).textOpacity : (nodeData.style?.textOpacity !== undefined ? nodeData.style.textOpacity : 1)

  // Text formatting
  const fontSize = (nodeData as any).fontSize !== undefined ? (nodeData as any).fontSize : (nodeData.style?.fontSize || 12)
  const fontWeight = (nodeData as any).fontWeight || nodeData.style?.fontWeight || '400'
  const fontFamily = (nodeData as any).fontFamily || nodeData.style?.fontFamily || 'Inter, system-ui, sans-serif'
  const textAlign = (nodeData as any).textAlign || nodeData.style?.textAlign || 'left'
  const lineHeight = (nodeData as any).lineHeight !== undefined ? (nodeData as any).lineHeight : (nodeData.style?.lineHeight || 1.5)
  const textDecoration = (nodeData as any).textDecoration || nodeData.style?.textDecoration || 'none'

  // Text behavior
  const wordWrap = (nodeData as any).wordWrap !== undefined ? (nodeData as any).wordWrap : (nodeData.style?.wordWrap !== undefined ? nodeData.style.wordWrap : true)
  const textOverflow = (nodeData as any).textOverflow || nodeData.style?.textOverflow || 'ellipsis'
  const textPadding = (nodeData as any).textPadding !== undefined ? (nodeData as any).textPadding : (nodeData.style?.textPadding || 12)
  const letterSpacing = (nodeData as any).letterSpacing !== undefined ? (nodeData as any).letterSpacing : (nodeData.style?.letterSpacing || 0)

  // Use measured width/height from React Flow, or defaults
  const nodeWidth = width || 320
  const nodeHeight = height || 400

  // Fallback for basic node
  if (!endpointContent) {
    return (
      <div
        className="bg-white border-2 border-gray-300 rounded shadow-md flex flex-col"
        style={{ width: nodeWidth, height: nodeHeight, minWidth: 250, minHeight: 150 }}
      >
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-300 font-semibold">
          {nodeData.label}
        </div>
        <div className="px-3 py-2 text-sm text-gray-500 italic">
          No endpoint content defined
        </div>
        <NodeConnectionHandles />
      </div>
    )
  }

  return (
    <>
      {/* Interactive Enhancements - Only apply when hover is supported (not during export) */}
      <style>
        {`
          /* Interactive polish - automatically stripped during PNG/SVG export */
          @media (hover: hover) {
            .endpoint-item:hover {
              background-color: #f9fafb !important;
              transition: background-color 150ms ease;
            }

            /* Code blocks: light for export, dark for interactive */
            .code-block {
              background-color: #1e293b !important;  /* Dark slate */
              border: none !important;
              transition: background-color 150ms ease, border 150ms ease;
            }

            .code-block code {
              color: #e2e8f0 !important;  /* Light gray text */
            }
          }
        `}
      </style>

      {selected && (
        <NodeResizer
          minWidth={250}
          minHeight={150}
        />
      )}

      <div
        className="rounded shadow-md flex flex-col"
        style={{
          width: nodeWidth,
          height: nodeHeight,
          borderWidth: borderWidth,
          borderStyle: borderStyle,
          borderColor: borderColor,
          backgroundColor: bodyBg,
          opacity: fillOpacity,
          overflow: 'hidden',
          fontFamily: fontFamily,
          color: textColor
        }}
      >
        {/* Entity Name Header - Fixed height, export-optimized spacing */}
        <div
          className="text-center flex-shrink-0"
          style={{
            backgroundColor: headerBg,
            color: headerTextColor,
            fontSize: fontSize + 4,
            fontWeight: fontWeight,
            fontFamily: fontFamily,
            padding: '16px',  // Fixed 16px for professional export spacing
            textAlign: textAlign as any,
            textDecoration: textDecoration,
            letterSpacing: `${letterSpacing}px`,
            lineHeight: lineHeight,
            overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
            textOverflow: textOverflow === 'visible' ? undefined : (textOverflow as any),
            whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap')
          }}
          title={endpointContent.entityName}
        >
          {endpointContent.entityName}
        </div>

        {/* Entity Description - Fixed height */}
        <div
          className="bg-gray-50 border-b-2 flex-shrink-0"
          style={{
            borderColor,
            fontSize: fontSize - 1,
            fontFamily: fontFamily,
            padding: `${textPadding}px`,
            letterSpacing: `${letterSpacing}px`,
            opacity: textOpacity * 0.8,
            overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
            textOverflow: textOverflow === 'visible' ? undefined : (textOverflow as any),
            whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap')
          }}
          title={endpointContent.description}
        >
          {endpointContent.description}
        </div>

        {/* Endpoints Grouped by Method - Scrollable */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ overflowX: textOverflow === 'visible' ? 'visible' : 'hidden', minHeight: 0 }}
        >
          {endpointContent.endpoints.map((methodGroup, groupIndex) => (
            <div key={groupIndex}>
              {/* Method Header (Collapsible) */}
              <div
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 border-b border-gray-200"
                onClick={() => toggleMethod(methodGroup.method)}
              >
                {expandedMethods[methodGroup.method] ? (
                  <ChevronDown className="w-3 h-3 text-gray-500" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-gray-500" />
                )}

                {/* Method Badge - Soft tinted background */}
                <span
                  className="px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0"
                  style={{
                    backgroundColor: methodColors[methodGroup.method]?.bg || '#f3f4f6',
                    color: methodColors[methodGroup.method]?.text || '#374151'
                  }}
                >
                  {methodGroup.method}
                </span>

                <span className="text-xs font-semibold text-gray-600">
                  {methodGroup.items.length} {methodGroup.items.length === 1 ? 'endpoint' : 'endpoints'}
                </span>
              </div>

              {/* Endpoint Items */}
              {expandedMethods[methodGroup.method] && (
                <div className="bg-white">
                  {methodGroup.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="endpoint-item border-b border-gray-100"
                      style={{
                        padding: '12px 16px',  // Export-safe padding
                        minWidth: 0
                      }}
                    >
                      {/* Path */}
                      <div
                        className="font-medium text-gray-800 mb-1"
                        style={{
                          fontSize,
                          fontFamily: 'JetBrains Mono, monospace',
                          overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
                          textOverflow: textOverflow === 'visible' ? undefined : 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={item.path}
                      >
                        {item.path}
                      </div>

                      {/* Description */}
                      <div
                        className="text-gray-600 text-sm"
                        style={{
                          fontSize: fontSize - 1,
                          overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
                          textOverflow: textOverflow === 'visible' ? undefined : 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={item.description}
                      >
                        {item.description}
                      </div>

                      {/* Optional Parameters */}
                      {item.parameters && item.parameters.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Parameters:
                          </div>
                          <div className="space-y-1">
                            {item.parameters.map((param, paramIndex) => (
                              <div
                                key={paramIndex}
                                className="flex items-center gap-2 text-xs"
                              >
                                <span
                                  className="font-medium"
                                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                                >
                                  {param.name}
                                </span>
                                <span className="text-gray-500">
                                  {param.type}
                                </span>
                                {param.required && (
                                  <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                                    required
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Optional Responses */}
                      {item.responses && Object.keys(item.responses).length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Responses:
                          </div>
                          <div className="space-y-1">
                            {Object.entries(item.responses).map(([code, desc]) => (
                              <div
                                key={code}
                                className="flex items-center gap-2 text-xs"
                              >
                                <span
                                  className={`px-1.5 py-0.5 rounded font-medium ${
                                    code.startsWith('2')
                                      ? 'bg-green-100 text-green-700'
                                      : code.startsWith('4')
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                                >
                                  {code}
                                </span>
                                <span className="text-gray-600">{desc}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Request Example - Light background for export, dark for interactive */}
                      {item.requestExample && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Request Body:
                          </div>
                          <pre
                            className="code-block rounded overflow-x-auto"
                            style={{
                              backgroundColor: '#f8fafc',  // Light gray for export (saves ink)
                              border: '1px solid #cbd5e1',  // Crisp border for export
                              padding: '16px',  // Export-safe padding
                              fontSize: fontSize - 2,
                              fontFamily: 'JetBrains Mono, monospace',
                              maxHeight: 150,
                              overflowY: 'auto'
                            }}
                          >
                            <code style={{ color: '#1f2937' }}>
                              {item.requestExample}
                            </code>
                          </pre>
                        </div>
                      )}

                      {/* Response Example - Light background for export, dark for interactive */}
                      {item.responseExample && (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-gray-500 mb-1">
                            Response Body:
                          </div>
                          <pre
                            className="code-block rounded overflow-x-auto"
                            style={{
                              backgroundColor: '#f8fafc',  // Light gray for export (saves ink)
                              border: '1px solid #cbd5e1',  // Crisp border for export
                              padding: '16px',  // Export-safe padding
                              fontSize: fontSize - 2,
                              fontFamily: 'JetBrains Mono, monospace',
                              maxHeight: 150,
                              overflowY: 'auto'
                            }}
                          >
                            <code style={{ color: '#1f2937' }}>
                              {item.responseExample}
                            </code>
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <NodeConnectionHandles />
      </div>
    </>
  )
}

export default memo(APIEndpointNode)
